import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Wifi, 
  Car, 
  Utensils, 
  Wine, 
  Dumbbell, 
  Waves, 
  Sparkles, 
  Building, 
  Plane, 
  Clock, 
  Briefcase, 
  Heart, 
  Ban, 
  Snowflake, 
  Bath, 
  Tv, 
  Monitor, 
  Coffee, 
  Wind, 
  Accessibility, 
  Eye, 
  Save,
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import hotelService, { HotelAmenities } from '../../services/hotelService';

interface HotelAmenitiesProps {
  hotelId: number;
}

const HotelAmenitiesComponent: React.FC<HotelAmenitiesProps> = ({ hotelId }) => {
  const [amenities, setAmenities] = useState<HotelAmenities | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadAmenities();
  }, [hotelId]);

  const loadAmenities = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await hotelService.getHotelAmenities(hotelId);
      
      // If no amenities exist yet, create default amenities object
      if (response.data === null) {
        const defaultAmenities: HotelAmenities = {
          hotel_id: hotelId,
          free_wifi: false,
          parking_available: false,
          parking_type: null,
          restaurant: false,
          bar_lounge: false,
          fitness_center: false,
          swimming_pool: false,
          spa_wellness: false,
          elevator: false,
          airport_shuttle: false,
          front_desk_24x7: false,
          business_center: false,
          pet_friendly: false,
          non_smoking_property: false,
          air_conditioning: false,
          private_bathroom: false,
          flat_screen_tv: false,
          work_desk: false,
          minibar: false,
          coffee_tea_maker: false,
          hairdryer: false,
          wheelchair_access: false,
          braille_signage: false,
          accessible_bathrooms: false,
        };
        setAmenities(defaultAmenities);
      } else {
        setAmenities(response.data);
      }
    } catch (err) {
      setError('Failed to load hotel amenities');
      console.error('Error loading amenities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAmenityChange = (field: keyof HotelAmenities, value: boolean | string | null) => {
    setAmenities(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amenities) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await hotelService.updateHotelAmenities(hotelId, amenities);
      setSuccess('Hotel amenities updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update hotel amenities');
      console.error('Error updating amenities:', err);
    } finally {
      setSaving(false);
    }
  };

  const amenityGroups = [
    {
      title: 'Property Amenities',
      icon: Building,
      amenities: [
        { key: 'free_wifi', label: 'Free WiFi', icon: Wifi },
        { key: 'parking_available', label: 'Parking Available', icon: Car },
        { key: 'restaurant', label: 'Restaurant', icon: Utensils },
        { key: 'bar_lounge', label: 'Bar/Lounge', icon: Wine },
        { key: 'fitness_center', label: 'Fitness Center', icon: Dumbbell },
        { key: 'swimming_pool', label: 'Swimming Pool', icon: Waves },
        { key: 'spa_wellness', label: 'Spa & Wellness', icon: Sparkles },
        { key: 'elevator', label: 'Elevator', icon: Building },
        { key: 'airport_shuttle', label: 'Airport Shuttle', icon: Plane },
        { key: 'front_desk_24x7', label: '24/7 Front Desk', icon: Clock },
        { key: 'business_center', label: 'Business Center', icon: Briefcase },
        { key: 'pet_friendly', label: 'Pet Friendly', icon: Heart },
        { key: 'non_smoking_property', label: 'Non-Smoking Property', icon: Ban }
      ]
    },
    {
      title: 'Room Amenities',
      icon: Building,
      amenities: [
        { key: 'air_conditioning', label: 'Air Conditioning', icon: Snowflake },
        { key: 'private_bathroom', label: 'Private Bathroom', icon: Bath },
        { key: 'flat_screen_tv', label: 'Flat Screen TV', icon: Tv },
        { key: 'work_desk', label: 'Work Desk', icon: Monitor },
        { key: 'minibar', label: 'Minibar', icon: Wine },
        { key: 'coffee_tea_maker', label: 'Coffee/Tea Maker', icon: Coffee },
        { key: 'hairdryer', label: 'Hairdryer', icon: Wind }
      ]
    },
    {
      title: 'Accessibility Features',
      icon: Accessibility,
      amenities: [
        { key: 'wheelchair_access', label: 'Wheelchair Access', icon: Accessibility },
        { key: 'braille_signage', label: 'Braille Signage', icon: Eye },
        { key: 'accessible_bathrooms', label: 'Accessible Bathrooms', icon: Bath }
      ]
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-white">Loading amenities...</span>
      </div>
    );
  }

  if (!amenities) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-white">Loading amenities...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {success && (
        <div className="flex items-center space-x-2 p-4 bg-green-900/20 border border-green-800 rounded-md">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-400">{success}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center space-x-2 p-4 bg-red-900/20 border border-red-800 rounded-md">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {amenityGroups.map((group, groupIndex) => (
          <Card key={groupIndex} className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <group.icon className="w-5 h-5" />
                <span>{group.title}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.amenities.map((amenity) => (
                  <div key={amenity.key} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id={amenity.key}
                      checked={amenities[amenity.key as keyof HotelAmenities] as boolean || false}
                      onChange={(e) => handleAmenityChange(amenity.key as keyof HotelAmenities, e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <Label htmlFor={amenity.key} className="text-white flex items-center space-x-2 cursor-pointer">
                      <amenity.icon className="w-4 h-4" />
                      <span>{amenity.label}</span>
                    </Label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Parking Type */}
        {amenities.parking_available && (
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <Car className="w-5 h-5" />
                <span>Parking Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="parking_type" className="text-gray-300">Parking Type</Label>
                  <select
                    id="parking_type"
                    value={amenities.parking_type || ''}
                    onChange={(e) => handleAmenityChange('parking_type', e.target.value || null)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md mt-1"
                  >
                    <option value="">Select parking type</option>
                    <option value="Free">Free</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}


        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Amenities
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default HotelAmenitiesComponent;
